import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { CheckboxField } from '@/components/forms/CheckboxField';
import { Checkbox } from '@/components/ui/Checkbox';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { TextField } from '@/components/forms/TextField';
import { SelectField } from '@/components/forms/SelectField';
import { TextareaField } from '@/components/forms/TextareaField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { FUNDING_STAGE_OPTIONS } from '@/lib/constants/funding-stages';
import type { InvestorProfileFormValues } from './schema';
import {
  LEAD_OR_FOLLOW_OPTIONS,
  MARKET_MODEL_FIELDS,
  MARKET_MODEL_OPTIONS,
  MARKET_MODEL_TONE_BY_VALUE,
  RECURRING_REVENUE_OPTIONS,
  REVENUE_OPTIONS,
} from './investor-options';

const S = UI_STRINGS.INVESTOR_PROFILE;

export interface PreferencesEditorProps {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
}

function TicketAndRevenueFields({ control, register, errors }: PreferencesEditorProps) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <FieldLabel label={S.TICKET_SIZE} variant="caps" />
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Whole numbers, grouped as typed (MYN-277); the max is checked against the min only when both are filled. */}
          <Controller
            control={control}
            name="prefAmountLow"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={S.MIN_INVESTMENT}
                labelVariant="caps"
                error={errors?.prefAmountLow?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="prefAmountHigh"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={S.MAX_INVESTMENT}
                labelVariant="caps"
                error={errors?.prefAmountHigh?.message}
              />
            )}
          />
        </div>
      </div>
      {/* Two short picks share a row. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          {...register('prefMinRevenue')}
          label={S.MIN_REVENUE}
          labelVariant="caps"
          options={REVENUE_OPTIONS}
          placeholder="Select a minimum"
          error={errors?.prefMinRevenue?.message}
        />
        <SelectField
          {...register('prefRecurringRevenue')}
          label={S.RECURRING_REVENUE}
          labelVariant="caps"
          options={RECURRING_REVENUE_OPTIONS}
          placeholder="Select a preference"
          error={errors?.prefRecurringRevenue?.message}
        />
      </div>
    </>
  );
}

function StagesField({ register, errors }: Omit<PreferencesEditorProps, 'control'>) {
  return (
    <div className="flex flex-col gap-3">
      <FieldLabel label={S.PREFERRED_STAGES} variant="caps" />
      <p className="text-p6 text-slate italic">{S.PREFERRED_STAGES_HINT}</p>
      <fieldset className="flex flex-wrap gap-4">
        {FUNDING_STAGE_OPTIONS.map((option) => (
          <Checkbox
            key={option.value}
            value={option.value}
            label={option.label}
            {...register('preferredStages')}
          />
        ))}
      </fieldset>
      {errors?.preferredStages?.message && (
        <p role="alert" className="text-danger text-xs">
          {errors.preferredStages.message}
        </p>
      )}
    </div>
  );
}

function MarketModelFields({ control, errors }: Pick<PreferencesEditorProps, 'control' | 'errors'>) {
  return (
    <div className="flex flex-col gap-3">
      <FieldLabel label={S.MARKET_MODEL_PREFERENCES} variant="caps" />
      {MARKET_MODEL_FIELDS.map(({ name, label }) => (
        <div key={name} className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-p6 text-navy font-semibold">{label}</span>
            <Controller
              control={control}
              name={name}
              render={({ field }) => (
                <SegmentedControl
                  label={label}
                  options={MARKET_MODEL_OPTIONS}
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  toneByValue={MARKET_MODEL_TONE_BY_VALUE}
                />
              )}
            />
          </div>
          {/* Any rule that still fails shows its text here: a control with no error line fails silently. */}
          {errors?.[name]?.message && (
            <p role="alert" className="text-danger text-xs">
              {errors[name]?.message}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

/** Edit form for the Investment Preferences card (FR-175). */
export function PreferencesEditor({ control, register, errors }: PreferencesEditorProps) {
  return (
    <div className="flex flex-col gap-6">
      <TicketAndRevenueFields control={control} register={register} errors={errors} />
      <StagesField register={register} errors={errors} />
      <MarketModelFields control={control} errors={errors} />
      <div className="grid items-end gap-4 sm:grid-cols-2">
        <SelectField
          {...register('prefLeadOrFollow')}
          label={S.LEAD_OR_FOLLOW}
          labelVariant="caps"
          options={LEAD_OR_FOLLOW_OPTIONS}
          placeholder="Select an option"
          error={errors?.prefLeadOrFollow?.message}
        />
        <CheckboxField
          {...register('activelyDeploying')}
          variant="toggle"
          label={S.ACTIVELY_DEPLOYING}
        />
      </div>
      <TextareaField
        {...register('prefOtherCriteria')}
        label={S.OTHER_CRITERIA}
        labelVariant="caps"
        rows={3}
        error={errors?.prefOtherCriteria?.message}
      />
    </div>
  );
}
